import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Box } from '@theme-ui/components';
import { gql, useQuery } from '@apollo/client';

import Checkbox from '@app/components/input/Checkbox';
import { isNotNullish } from '@app/helpers/predicate';

import type { TermsQuery } from './__generated__/TermsQuery';

interface TermsAndConditionsProps {
  setTermsAccepted(val: boolean): void;
  error: boolean;
  cartId?: string;
}

const TermsAndConditions: FC<TermsAndConditionsProps> = ({
  setTermsAccepted,
  error,
  cartId,
}) => {
  const { data, loading } = useQuery<TermsQuery>(TERMS_AND_CONDITIONS_QUERY, {
    variables: { cartId },
    skip: !cartId,
  });
  const terms = data?.magento2?.cart?.availableAgreements?.filter(isNotNullish);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    const values = Object.values(checked);
    if (values.length === terms?.length) {
      setTermsAccepted(values.every(Boolean));
    }
  }, [checked, setTermsAccepted, terms?.length]);

  if (loading || !cartId) return null;

  return (
    <Box>
      {terms?.map((term) => (
        <Box py={2} key={term.id}>
          {/*eslint-disable-next-line jsx-a11y/label-has-associated-control*/}
          <label
            data-testid={`term-input-${term.name}`}
            htmlFor={`term-${term.id}`}
            key={term.id}
          >
            <Checkbox
              type="checkbox"
              name={`term-${term.name}`}
              id={`term-${term.id}`}
              aria-invalid={error}
              onChange={(e) => {
                setChecked({
                  ...checked,
                  [term.id]: e.target.checked,
                });
              }}
              checked={checked[term.id] === true}
            />
            <span dangerouslySetInnerHTML={{ __html: term.checkboxText }} />
          </label>
        </Box>
      ))}
    </Box>
  );
};

export default TermsAndConditions;

export const TERMS_AND_CONDITIONS_QUERY = gql`
  query TermsQuery($cartId: String!) {
    magento2 {
      cart(cart_id: $cartId) {
        availableAgreements {
          name
          id
          checkboxText
        }
      }
    }
  }
`;
